import {
  INDUSTRIAL_FASTENING_METRICS,
  INDUSTRIAL_FASTENING_POINTS,
  INDUSTRIAL_PRODUCTION_METRICS,
  INDUSTRIAL_USE_CASE_GROUPS,
  type IndustrialUseCaseImage,
} from '@/data/shared/industrialCopy';

import {
  INDUSTRIAL_CASES,
  INDUSTRIAL_SOUND_CHIPS,
  IndustrialCaseFigure,
  IndustrialCaseImage,
  IndustrialCaseMetric,
  IndustrialCaseNarrativeSection,
  IndustrialCaseSource,
} from './industrialCaseData';

type Translate = (key: string) => string;

export type IndustrialSoundChip = {
  label: string;
  highlighted: boolean;
};

export type IndustrialCaseCardData = {
  id: string;
  caseNumber: string;
  customerName: string;
  title: string;
  meta: string;
  targetSound: string;
  summary: string;
  metric: IndustrialCaseMetric;
  imgUrl: IndustrialCaseImage;
  detail: {
    overview: string;
    challenges: string[];
    outcomes: IndustrialCaseMetric[];
    narrative: IndustrialCaseNarrativeSection[];
  };
  figure: IndustrialCaseFigure;
};

const INDUSTRIAL_MOTOR_AUDIO_SRCS = [
  '/sounds/industrial/motor-stage-00-normal.wav',
  '/sounds/industrial/motor-stage-01-anomaly.wav',
  '/sounds/industrial/motor-stage-02-anomaly.wav',
  '/sounds/industrial/motor-stage-03-anomaly.wav',
  '/sounds/industrial/motor-stage-04-anomaly.wav',
] as const;
const INDUSTRIAL_CONNECTOR_AUDIO_SRCS = {
  a: '/sounds/industrial/connector-click-a-factory.wav?v=strong-noise',
  b: '/sounds/industrial/connector-click-b-factory.wav?v=strong-noise',
} as const;

/**
 * ### IndustrialUseCaseGroupView
 *
 * One production use case with every string already resolved to the page
 * language, ready to render.
 */
export type IndustrialUseCaseGroupView = {
  /**
   * ### title
   *
   * Name of the use-case group.
   */
  title: string;

  /**
   * ### description
   *
   * The group's paragraph, carrying its figure and the condition that figure
   * was measured under.
   */
  description: string;

  /**
   * ### tags
   *
   * Inspection keywords describing the work, never its performance.
   */
  tags: Array<string>;

  /**
   * ### image
   *
   * Artwork of the group, passed through untranslated.
   */
  image: IndustrialUseCaseImage;

  /**
   * ### focusAreas
   *
   * Sound types singled out for this group, or `undefined` when none is
   * stated for it.
   */
  focusAreas?: Array<string>;
};

/**
 * ### IndustrialProductionMetricView
 *
 * One headline figure with its label and scope resolved to the page language.
 */
export type IndustrialProductionMetricView = {
  /**
   * ### value
   *
   * The figure itself, identical in every language.
   */
  value: string;

  /**
   * ### label
   *
   * What the figure measures.
   */
  label: string;

  /**
   * ### scope
   *
   * The conditions the figure was measured under, rendered next to the value
   * so the two are never read apart.
   */
  scope: string;
};

/**
 * ### useIndustrialUseCaseGroups
 *
 * Resolves the three production use cases into the page language. Artwork is
 * passed through as authored, since only the copy is translated.
 *
 * @param t Translator of the `common` namespace
 * @returns The use-case groups in the order they are published
 * @see {@link INDUSTRIAL_USE_CASE_GROUPS} The copy resolved here
 */
export function useIndustrialUseCaseGroups(
  t: Translate,
): Array<IndustrialUseCaseGroupView> {
  return INDUSTRIAL_USE_CASE_GROUPS.map((group) => ({
    title: t(group.title),
    description: t(group.description),
    tags: group.tags.map((tag) => t(tag)),
    image: group.image,
    focusAreas: group.focusAreas?.map((focusArea) => t(focusArea)),
  }));
}

/**
 * ### useIndustrialProductionMetrics
 *
 * Resolves the three published production figures into the page language.
 * `value` goes through the dictionary like every other string, and
 * `metricValueTranslations.test.ts` holds any translation to the same digits
 * as the authored value, so no language can drift to a different number.
 *
 * @param t Translator of the `common` namespace
 * @returns The figures in the order of the use-case groups
 * @see {@link INDUSTRIAL_PRODUCTION_METRICS} The figures resolved here
 */
export function useIndustrialProductionMetrics(
  t: Translate,
): Array<IndustrialProductionMetricView> {
  return INDUSTRIAL_PRODUCTION_METRICS.map((metric) => ({
    value: t(metric.value),
    label: t(metric.label),
    scope: t(metric.scope),
  }));
}

/**
 * ### IndustrialFasteningPointView
 *
 * One fastening defect with every string resolved to the page language.
 */
export type IndustrialFasteningPointView = {
  /**
   * ### title
   *
   * Name of the defect.
   */
  title: string;

  /**
   * ### description
   *
   * How the inspection separates that defect from a full engagement.
   */
  description: string;

  /**
   * ### tags
   *
   * Inspection keywords describing the work, never its performance.
   */
  tags: Array<string>;
};

/**
 * ### useIndustrialFasteningPoints
 *
 * Resolves the connector engagement deep dive into the page language.
 *
 * @param t Translator of the `common` namespace
 * @returns The defects in the order they are published
 * @see {@link INDUSTRIAL_FASTENING_POINTS} The copy resolved here
 */
export function useIndustrialFasteningPoints(
  t: Translate,
): Array<IndustrialFasteningPointView> {
  return INDUSTRIAL_FASTENING_POINTS.map((point) => ({
    title: t(point.title),
    description: t(point.description),
    tags: point.tags.map((tag) => t(tag)),
  }));
}

/**
 * ### useIndustrialFasteningMetrics
 *
 * Resolves the connector engagement figures into the page language. A value
 * that carries words (`1초 내외`, `85dB 이상`) is translated too;
 * `metricValueTranslations.test.ts` holds every translation to the same
 * digits as the authored value, so no language can drift to a different
 * number.
 *
 * @param t Translator of the `common` namespace
 * @returns The figures in the order of the fastening deep dive
 * @see {@link INDUSTRIAL_FASTENING_METRICS} The figures resolved here
 */
export function useIndustrialFasteningMetrics(
  t: Translate,
): Array<IndustrialProductionMetricView> {
  return INDUSTRIAL_FASTENING_METRICS.map((metric) => ({
    value: t(metric.value),
    label: t(metric.label),
    scope: t(metric.scope),
  }));
}

export function useIndustrialSoundChips(t: Translate) {
  return INDUSTRIAL_SOUND_CHIPS.map((chip) => ({
    label: t(chip.label),
    highlighted: Boolean(chip.highlighted),
  }));
}

export function useIndustrialMotorSamples(t: Translate) {
  return [
    {
      analyticsId: 'motor_normal_00',
      stage: '00',
      level: 0,
      title: t('정상 모터 사운드'),
      description: t('기준이 되는 정상 회전 구간의 소리예요'),
      src: INDUSTRIAL_MOTOR_AUDIO_SRCS[0],
    },
    {
      analyticsId: 'motor_anomaly_01',
      stage: '01',
      level: 25,
      title: t('초기 이상 사운드'),
      description: t('정상음 위에 작은 마찰 신호가 섞이기 시작해요'),
      src: INDUSTRIAL_MOTOR_AUDIO_SRCS[1],
    },
    {
      analyticsId: 'motor_anomaly_02',
      stage: '02',
      level: 50,
      title: t('중간 이상 사운드'),
      description: t('반복 노이즈와 회전 편차가 더 분명해져요'),
      src: INDUSTRIAL_MOTOR_AUDIO_SRCS[2],
    },
    {
      analyticsId: 'motor_anomaly_03',
      stage: '03',
      level: 75,
      title: t('고위험 이상 사운드'),
      description: t('정상 구간과 구분되는 거친 패턴이 나타나요'),
      src: INDUSTRIAL_MOTOR_AUDIO_SRCS[3],
    },
    {
      analyticsId: 'motor_anomaly_04',
      stage: '04',
      level: 100,
      title: t('정비 검토 사운드'),
      description: t('교체 또는 정비 판단이 필요한 이상음이에요'),
      src: INDUSTRIAL_MOTOR_AUDIO_SRCS[4],
    },
  ];
}

export function useIndustrialConnectorSamples(t: Translate) {
  return [
    {
      analyticsId: 'connector_click_a',
      key: 'A',
      title: t('커넥터 체결 클릭 A'),
      description: t('정상 체결 시 발생하는 선명한 락킹음을 비교해요'),
      src: INDUSTRIAL_CONNECTOR_AUDIO_SRCS.a,
      transientSrc: '/sounds/industrial/connector-click-a.wav',
    },
    {
      analyticsId: 'connector_click_b',
      key: 'B',
      title: t('커넥터 체결 클릭 B'),
      description: t('소프트 체결과 미체결 가능성을 나눠 확인해요'),
      src: INDUSTRIAL_CONNECTOR_AUDIO_SRCS.b,
      transientSrc: '/sounds/industrial/connector-click-b.wav',
    },
  ];
}

function translateMetric(t: Translate, metric: IndustrialCaseMetric) {
  return {
    value: t(metric.value),
    label: t(metric.label),
  };
}

function translateFigure(
  t: Translate,
  figure: IndustrialCaseFigure,
): IndustrialCaseFigure {
  if (figure.kind === 'flow') return figure;

  return {
    kind: 'accuracy',
    points: figure.points.map((point) => ({
      ...point,
      displayValue: t(point.displayValue),
      label: t(point.label),
    })),
  };
}

export function translateIndustrialCase(
  t: Translate,
  caseItem: IndustrialCaseSource,
): IndustrialCaseCardData {
  return {
    id: caseItem.id,
    caseNumber: caseItem.caseNumber,
    customerName: t(caseItem.customerName),
    title: t(caseItem.title),
    meta: t(caseItem.meta),
    targetSound: t(caseItem.targetSound),
    summary: t(caseItem.summary),
    metric: translateMetric(t, caseItem.metric),
    imgUrl: caseItem.imgUrl,
    detail: {
      overview: t(caseItem.detail.overview),
      challenges: caseItem.detail.challenges.map((challenge) => t(challenge)),
      outcomes: caseItem.detail.outcomes.map((outcome) =>
        translateMetric(t, outcome),
      ),
      narrative: caseItem.detail.narrative.map((section) => ({
        heading: t(section.heading),
        paragraphs: section.paragraphs.map((paragraph) => t(paragraph)),
      })),
    },
    figure: translateFigure(t, caseItem.figure),
  };
}

export function useIndustrialCaseCards(t: Translate) {
  return INDUSTRIAL_CASES.map((caseItem) =>
    translateIndustrialCase(t, caseItem),
  );
}
